import { useEffect, useState } from 'react';
import { Building2, UserRound } from 'lucide-react';
import type { ProjectoCampaign, ProjectoStatus } from '../../types/projecto';
import { PROJECTO_STATUS_CONFIG } from '../../types/projecto';
import { supabase } from '../../lib/supabase';
import { listStaffAccounts } from '../../services/projectoService';
import { log } from '../../lib/logger';
import { FormBlock } from '../ui/FormBlock';

interface Client {
  id: string;
  name: string;
}

interface Props {
  data: Partial<ProjectoCampaign>;
  onChange: (updates: Partial<ProjectoCampaign>) => void;
}

export function ProjectoGeneralTab({ data, onChange }: Props) {
  const [clients, setClients] = useState<Client[]>([]);
  const [accounts, setAccounts] = useState<{ user_id: string; name: string }[]>([]);

  useEffect(() => {
    supabase.from('clients').select('id, name').order('name').then(({ data }) => {
      setClients(data ?? []);
    });
    listStaffAccounts().then(setAccounts).catch((err) => log.warn('[ProjectoGeneralTab] Accounts not loaded:', err));
  }, []);

  const handleClientChange = (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    onChange({ client_id: clientId || null, client_name: client?.name ?? '' });
  };

  const handleStartDate = (value: string) => {
    const start = value || null;
    const updates: Partial<ProjectoCampaign> = { start_date: start };
    if (start && data.finish_date && data.finish_date < start) updates.finish_date = start;
    onChange(updates);
  };

  return (
    <div className="space-y-7 pb-10 pt-2">
      <FormBlock title="Projeto">
        <div className="cm-grid">
          <div className="cm-field">
            <label htmlFor="projecto-name" className="cm-label">Nome do projeto<span className="cm-req">*</span></label>
            <input
              id="projecto-name"
              data-required-field="projecto-name"
              className="cm-input"
              placeholder="ex.: Festival de Verão 2026"
              value={data.name ?? ''}
              onChange={(e) => onChange({ name: e.target.value })}
            />
          </div>
          <div className="cm-field">
            <label htmlFor="projecto-reference" className="cm-label">Referência do cliente</label>
            <input
              id="projecto-reference"
              className="cm-input"
              placeholder="Opcional"
              value={data.reference ?? ''}
              onChange={(e) => onChange({ reference: e.target.value })}
            />
          </div>
          <div className="cm-field">
            <label htmlFor="projecto-client" className="cm-label">Cliente</label>
            <div className="cm-with-icon">
              <Building2 className="cm-input-icon" />
              <select id="projecto-client" className="cm-select pl-9" value={data.client_id ?? ''} onChange={(e) => handleClientChange(e.target.value)}>
                <option value="">Escolher cliente</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="cm-field">
            <label htmlFor="projecto-brand" className="cm-label">Marca</label>
            <input
              id="projecto-brand"
              className="cm-input"
              placeholder="ex.: G-Shock"
              value={data.brand ?? ''}
              onChange={(e) => onChange({ brand: e.target.value })}
            />
          </div>
        </div>
        <div className="cm-grid mt-5">
          <div className="cm-field">
            <label htmlFor="projecto-account" className="cm-label">Account responsável</label>
            <div className="cm-with-icon">
              <UserRound className="cm-input-icon" />
              <select id="projecto-account" className="cm-select pl-9" value={data.account_id ?? ''} onChange={(e) => onChange({ account_id: e.target.value || null })}>
                <option value="">Sem account</option>
                {accounts.map((a) => <option key={a.user_id} value={a.user_id}>{a.name}</option>)}
              </select>
            </div>
            <p className="text-xs text-gray-500 mt-1">Recebe um aviso quando o cliente paga uma fatura deste projeto.</p>
          </div>
          <div className="cm-field">
            <label htmlFor="projecto-status" className="cm-label">Estado</label>
            <select id="projecto-status" className="cm-select" value={data.status ?? 'Draft'} onChange={(e) => onChange({ status: e.target.value as ProjectoStatus })}>
              {Object.entries(PROJECTO_STATUS_CONFIG).map(([s, cfg]) => <option key={s} value={s}>{cfg.label}</option>)}
            </select>
          </div>
          <div className="cm-field">
            <label htmlFor="projecto-start" className="cm-label">Início</label>
            <input id="projecto-start" type="date" className="cm-date" value={data.start_date ?? ''} onChange={(e) => handleStartDate(e.target.value)} />
          </div>
          <div className="cm-field">
            <label htmlFor="projecto-finish" className="cm-label">Fim</label>
            <input id="projecto-finish" type="date" className="cm-date" min={data.start_date ?? undefined} value={data.finish_date ?? ''} onChange={(e) => onChange({ finish_date: e.target.value || null })} />
          </div>
        </div>
      </FormBlock>

      <FormBlock title="Ficheiros e notas">
        <div className="space-y-5">
          <div className="cm-field">
            <label htmlFor="projecto-drive" className="cm-label">Pasta do projeto</label>
            <input
              id="projecto-drive"
              type="url"
              className="cm-input"
              placeholder="https://drive.google.com/..."
              value={data.drive_link ?? ''}
              onChange={(e) => onChange({ drive_link: e.target.value || null })}
            />
          </div>
          <div className="cm-field">
            <label htmlFor="projecto-notes" className="cm-label">Notas e briefing</label>
            <textarea
              id="projecto-notes"
              rows={4}
              className="cm-textarea"
              placeholder="Contexto, objetivos, combinados com o cliente..."
              value={data.notes ?? ''}
              onChange={(e) => onChange({ notes: e.target.value || null })}
            />
          </div>
        </div>
      </FormBlock>
    </div>
  );
}
